import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import axiosInstance from "../../Axios";
import useAuth from "../../contexts/UserContext.tsx";

// Keep the perseus CSS here to prevent CSS spillover.
import "../../assets/perseus_new/lib/font-awesome.min.css";
import "../../assets/perseus_new/lib/katex/katex.css";
import "../../assets/perseus_new/lib/mathquill/mathquill.css";
import "../../assets/perseus_new/stylesheets/exercise-content-package/perseus.less";
import "../../assets/perseus_new/stylesheets/local-only/khan-exercise.css";
import "../../assets/perseus_new/stylesheets/local-only/khan-site.less";
import "../../assets/perseus_new/stylesheets/perseus-admin-package/editor.less";

const scriptSources = [
  "/DashboardV2/perseus/lib/babel-polyfills.min-compiled.js",
  "/DashboardV2/perseus/lib/jquery-compiled.js",
  "/DashboardV2/perseus/lib/underscore-compiled.js",
  "/DashboardV2/perseus/lib/react-with-addons-compiled.js",
  "/DashboardV2/perseus/lib/mathjax/2.1/MathJax.js?config=KAthJax-3d6f4e415c7ff2242f5279bfbcbb5c9f&delayStartupUntil=configured",
  "/DashboardV2/perseus/lib/katex/katex-compiled.js",
  "/DashboardV2/perseus/lib/mathquill/mathquill-basic-compiled.js",
  "/DashboardV2/perseus/lib/kas-compiled.js",
  "/DashboardV2/perseus/lib/i18n-compiled.js",
  "/DashboardV2/perseus/lib/calculator-compiled.js",
  "/DashboardV2/perseus/build/demo-perseus-compiled.js",
];

const exerciseColumns = [
  { id: "item_title", label: "Title" },
  { id: "item_status", label: "Status" },
];

export const IndividualProgressTable = ({ data, student_id }) => {
  const { user } = useAuth();

  const [exerciseStatus, setExerciseStatus] = useState();
  const [exerciseStatusLoading, setExerciseStatusLoading] = useState(false);
  const [attemptedQuestions, setAttemptedQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Accordion
  const [expanded, setExpanded] = useState(data[0].id);
  const handleAccordionExpandChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getStudentExerciseStatusData = async (chapter_id, student_id) => {
    try {
      setExerciseStatusLoading(true);
      const res = await axiosInstance.post("/getStudentChapterExerciseStatus", {
        auth_token: user.auth_token,
        chapter_id: chapter_id,
        student_id: student_id,
      });
      setExerciseStatus(res.data.data.student_chapter_excs_details);
    } catch (err) {
      console.error(err.message);
    } finally {
      setExerciseStatusLoading(false);
    }
  };

  const getAttemptedQuestions = async (attempt_id) => {
    setLoading(true);
    setAttemptedQuestions([]);
    setCurrentQuestionIndex(0);
    try {
      const res = await axiosInstance.post(
        "/getStudentsAttemptedQuestionDetails",
        {
          att_id: attempt_id,
          auth_token: user.auth_token,
        }
      );
      const questions = res.data.data;
      setAttemptedQuestions(questions);
      if (questions.length > 0) {
        renderQuestion(questions[0].question_json);
      } else {
        toast.error("No questions found");
        setOpen(false);
      }
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = (question) => {
    if (question) {
      setTimeout(() => {
        window.Khan.exerciseApi(JSON.parse(question), "renderer");
      }, 100);
    } else {
      toast.error("No questions found");
    }
  };

  const loadScriptsSequentially = async () => {
    for (let src of scriptSources) {
      await loadScript(src);
    }
  };

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.defer = true;
      script.className = "dynamic-script";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Script load error for ${src}`));
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    loadScriptsSequentially();

    return () => {
      const scripts = document.querySelectorAll(".dynamic-script");
      scripts.forEach((script) => script.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getStudentExerciseStatusData(expanded, student_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, student_id]);

  return (
    <div className="individual-progress-table-container mt-6">
      {data && (
        <>
          {data.map((item) => (
            <Accordion
              key={item.id}
              expanded={expanded === item.id}
              onChange={handleAccordionExpandChange(item.id)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${item.id}bh-content`}
                id={`${item.id}bh-header`}
                sx={{
                  backgroundColor:
                    expanded === item.id ? "rgb(226 232 240)" : "#fff",
                }}
              >
                <Typography sx={{ width: "33%", flexShrink: 0 }}>
                  {item.sec_name}
                </Typography>
                <div className="flex gap-10">
                  <div className="rounded-xl w-9 h-9 flex items-center justify-center text-white bg-[#eab80f]">
                    {item.NotStarted}
                  </div>
                  <div className="rounded-xl w-9 h-9 flex items-center justify-center text-white bg-[rgb(15,158,234)]">
                    {item.Started}
                  </div>
                  <div className="rounded-xl w-9 h-9 flex items-center justify-center text-white bg-[#ea271a]">
                    {item.Struggling}
                  </div>
                  <div className="rounded-xl w-9 h-9 flex items-center justify-center text-white bg-[#1b9808]">
                    {item.Completed}
                  </div>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {exerciseStatusLoading ? (
                  <div className="">
                    <Typography variant="h6" className="text-gray-400">
                      Loading...
                    </Typography>
                  </div>
                ) : (
                  <TableContainer className="tableContainer shadow-md">
                    <Table id="summary" className="mb-0">
                      <TableHead>
                        <TableRow>
                          {exerciseColumns.map((column) => (
                            <TableCell key={column.id}>
                              <span
                                style={{
                                  fontWeight: "bold",
                                  fontSize: "17px",
                                }}
                              >
                                {column.label}
                              </span>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {exerciseStatus &&
                          (exerciseStatus.length > 0 ? (
                            exerciseStatus.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell className="w-96">
                                  <span>{row.item_title}</span>
                                </TableCell>
                                <TableCell>
                                  <span
                                    className={`uppercase ${
                                      row.excs_mastery_status !==
                                        "Not Started" &&
                                      `text-[#8884d8] hover:underline cursor-pointer`
                                    }`}
                                    disabled={
                                      row.excs_mastery_status === "Not Started"
                                    }
                                    onClick={() => {
                                      getAttemptedQuestions(row.att_id);
                                      setOpen(true);
                                    }}
                                  >
                                    {row.excs_mastery_status}
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Typography
                                  variant="h6"
                                  className="text-gray-400"
                                >
                                  No data available
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          "& .MuiDialog-paper": {
            minWidth: "50vw",
            minHeight: "80vh",
          },
        }}
      >
        <DialogTitle className="opacity-95">
          Attempt Details
          <div className="text-sm opacity-60">
            Attempted questions by the student.
          </div>
          <div className="flex overflow-x-scroll gap-2 py-2">
            {attemptedQuestions &&
              attemptedQuestions.length > 0 &&
              attemptedQuestions.map((question, index) => {
                return (
                  <>
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentQuestionIndex(index);
                        renderQuestion(attemptedQuestions[index].question_json);
                      }}
                      className={`${
                        question.status === "CORRECT"
                          ? "bg-green-500"
                          : "bg-red-500"
                      } px-3 text-white rounded-lg ${
                        currentQuestionIndex === index
                          ? "border-black border-2"
                          : ""
                      }`}
                    >
                      {index + 1}
                    </button>
                  </>
                );
              })}
          </div>
        </DialogTitle>
        <DialogContent className="pointer-events-none">
          {loading && (
            <div className="flex items-center justify-center">
              <Typography variant="h6" className="text-gray-400">
                Loading...
              </Typography>
            </div>
          )}
          <div id="perseus-container"></div>
          {attemptedQuestions &&
            attemptedQuestions.length > 0 &&
            !loading &&
            attemptedQuestions[currentQuestionIndex].status === "CORRECT" && (
              <p className="font-semibold text-green-500">
                Correctly Answered!
              </p>
            )}
          {attemptedQuestions &&
            attemptedQuestions.length > 0 &&
            !loading &&
            attemptedQuestions[currentQuestionIndex].status === "INCORRECT" && (
              <p className="font-semibold text-red-500">
                Incorrectly Answered!
              </p>
            )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
